// src/components/ProgressionReport.js
import React, { Component } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import C from '../lib/Config';
import swal from 'sweetalert';
import Constants from '../lib/Constants';
import moment from 'moment';
export default class ProgressionReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            patientData: null,
            loading: true,
            schedule: [],
            treatmentCycles: [],
            chartData: {
                bloodReports: {
                    labels: [],
                    hemoglobin: [],
                    platelet: [],
                    anc: [],
                },
                weight: [],
                weightDates: [],
                stages: [],
                dosages: [],
                levels: [],
                chemoDates: []
            },
            activeTreatmentPlans: [],
            generatedSchedules: {},
            chemoSittingDetails: [],
            khoranaScore: '',
            initialAssessmentDetails: null,
            appointments: [],
            chemoTemplateDetails: {},
            appointmentReports: [],
            showModal: false,
            selectedReport: null,
            reportModal: false,
            reportMode: null,
            selectedApptReportData: null,
            reportsData: {}
        };
    }

    componentDidMount() {
        const patientId = this.props.patientId;
        this.fetchPatientDetails(patientId);
        this.getActiveTreatmentPlans(patientId);
        this.getChemoSittingDetails(patientId);
        this.getKhoranaScore(patientId); 
        this.getInitialAssessmentDetails(patientId);
        this.getAppointments(patientId);
        this.getAllAppointmentWeights(patientId);
    }

    componentDidUpdate(prevProps) {
        if (this.props.patientId !== prevProps.patientId) {
            this.fetchPatientDetails(this.props.patientId);
            this.getActiveTreatmentPlans(this.props.patientId);
            this.getChemoSittingDetails(this.props.patientId);
            this.getKhoranaScore(this.props.patientId);
            this.getInitialAssessmentDetails(this.props.patientId);
            this.getAppointments(this.props.patientId);
            this.getAllAppointmentWeights(this.props.patientId);
        }
    }

    fetchPatientDetails(patientId) {
        // Validate patientId before making API call
        if (!patientId || patientId === 'undefined' || patientId === undefined) {
            this.setState({ loading: false });
            return;
        }
        
        this.setState({ loading: true });
        fetch(C.getUrl() + `/userCase/getPatientSummaryDetails?patientId=${patientId}`)
            .then(response => response.json())
            .then(data => {
                const resObj = JSON.parse(data.message);
                if (resObj && resObj.patientDetailsDto) {
                    const dob = new Date(resObj.patientDetailsDto.dob);
                    const age = new Date().getFullYear() - dob.getFullYear();

                    this.setState({
                        patientData: { ...resObj.patientDetailsDto, age },
                        loading: false,
                        chartData: {
                            bloodReports: { ...this.state.chartData.bloodReports },
                            weight: resObj.weight || [],
                            weightDates: resObj.weightDates || [],
                            stages: resObj.stages || [],
                            dosages: resObj.dosages || [],
                            levels: [],
                            chemoDates: []
                        }
                    });
                } else {
                    this.setState({ loading: false });
                    swal("Error", "Failed to fetch patient details.", "error");
                }
            })
            .catch(error => {
                this.setState({ loading: false });
                swal("Error", "Failed to fetch patient details.", "error");
            });
    }

    getActiveTreatmentPlans(patientId) {
        return fetch(C.getUrl() + `/userCase/getTreatmentPlanDetails?patientId=${patientId}`)
            .then(res => res.json())
            .then(res => {
                let resObj = JSON.parse(res.message);
                let activeTreatmentPlans = resObj.patientTreatmentPlanDTOList || [];

                let generatedSchedules = {};
                activeTreatmentPlans.forEach(plan => {
                    if (plan.scheduleMstDTO.scheduleDays && plan.chemoStartDt && plan.cyclesPlanned) {
                        generatedSchedules[plan.patientTreatmentPlanId] = this.generateSchedule(
                            plan.scheduleMstDTO.scheduleDays,
                            plan.chemoStartDt,
                            plan.cyclesPlanned,
                            plan.patientChemoScheduleDTOList
                        );
                    }
                });

                this.setState({ activeTreatmentPlans, generatedSchedules });
            })
            .catch(err => {
                this.setState({ error: true });
            });
    }

    getChemoSittingDetails(patientId) {
        return fetch(C.getUrl() + '/userCase/getAllChemoSitting/' + patientId)
            .then(res => res.json())
            .then(res => {
                let resObj;
                try {
                    resObj = JSON.parse(res.message);
                } catch (error) {
                    resObj = [];
                }

                this.setState({ chemoSittingDetails: resObj }, () => {
                    resObj.forEach(sitting => {
                        this.getChemoTemplateForNurse(sitting.patChemoSittingId);
                    });
                });

                const levels = resObj.map(sitting => {
                    const templateDetails = this.state.chemoTemplateDetails[sitting.patChemoSittingId] || {};
                    const instructions = templateDetails.instructions || '';
                    const levelMatch = instructions.match(/Level:\s*(\d+)/);
                    return levelMatch ? parseInt(levelMatch[1], 10) : null;
                });

                const chemoDates = resObj.map(sitting => new Date(sitting.chemoDt).toLocaleDateString());

                this.setState(prevState => ({
                    chartData: {
                        ...prevState.chartData,
                        levels: levels.filter(level => level !== null),
                        chemoDates
                    }
                }));

            })
            .catch(err => { });
    }

    getChemoTemplateForNurse(chemoSittingId) {
        return fetch(C.getUrl() + `/userCase/getChemoTemplateForNurse/${chemoSittingId}`)
            .then(res => res.json())
            .then(res => {
                let resObj;
                try {
                    resObj = JSON.parse(res.message);
                } catch (error) {
                    resObj = {};
                }

                this.setState(prevState => ({
                    chemoTemplateDetails: {
                        ...prevState.chemoTemplateDetails,
                        [chemoSittingId]: resObj
                    }
                }));
            })
            .catch(err => { });
    }

    getKhoranaScore(patientId) {
        return fetch(C.getUrl() + '/protocol/getKhoranaScore/' + patientId)
            .then(res => res.json())
            .then(res => {
                if (res.statusCode === 200 && res.data != null) {
                    this.setState({ khoranaScore: res.data });
                }
            })
            .catch(err => { });
    }

    getInitialAssessmentDetails(patientId) {
        return fetch(C.getUrl() + '/userCase/getInitialAssessmentDetails?patientId=' + patientId)
            .then(res => res.json())
            .then(res => {
                let resObj;
                try {
                    resObj = JSON.parse(res.message);
                } catch (error) {
                    resObj = {};
                }
                this.setState({ initialAssessmentDetails: resObj.patInitiAssesmentDto });
            })
            .catch(err => { });
    }

    getAppointments(patientId) {
        return fetch(C.getUrl() + `/followupAppt/findAll/${patientId}`)
            .then(res => res.json())
            .then(res => {
                let resObj;
                try {
                    resObj = JSON.parse(res.message);
                } catch (error) {
                    resObj = {};
                }

                // Extract and sort appointments by date
                const sortedAppointments = (resObj.followUpAppointments || [])
                    .map(app => ({
                        appointmentDt: new Date(app.appointmentDt),
                        weight: app.weight,
                        reports: app.appointmentReports || [], // CBC, Biochemistry reports are part of appointments
                        adverseEffects: app.followupAdverseEffectDTOList || [],
                    }))
                    .sort((a, b) => a.appointmentDt - b.appointmentDt); // Sorting by appointment date

                const weightData = sortedAppointments.map(app => app.weight);
                const weightDates = sortedAppointments.map(app =>
                    this.formatDate(app.appointmentDt, 'dd-MM-yy')
                );

                // Collect all reports from appointments
                const appointmentReports = sortedAppointments.flatMap(app => app.reports);

                // Update state with sorted appointment data
                this.setState({
                    appointments: sortedAppointments,
                    appointmentReports: appointmentReports,
                    chartData: {
                        ...this.state.chartData,
                        weight: weightData,
                        weightDates: weightDates,
                    },
                });

                // Fetch detailed report data for each appointment report
                appointmentReports.forEach(report => {
                    this.getReportDetails(report.apptXReportId, report.reportMasterDTO.reportName);
                });
            })
            .catch(() => {});
    }
    
      
    
    getAllAppointmentWeights(patientId) {
                return fetch(C.getUrl() + `/followupAppt/findAll/${patientId}`)
                    .then(res => res.json())
                    .then(res => {
                        let resObj;
                        try {
                            resObj = (res.message && res.message !== 'No record found.') ? JSON.parse(res.message) : {};
                        } catch (error) {
                            resObj = {};
                        }
            
                        // Include main appointment along with follow-up appointments
                        const mainAppointment = {
                            appointmentDt: resObj.appointmentDt,
                            weight: resObj.weight,
                        };
                        const allAppointments = [mainAppointment, ...(resObj.followUpAppointments || [])];
            
                        // Process weight data from combined appointments
                        const weightData = allAppointments.map(app => {
                            const weight = app.weight;
                            const numericWeight = typeof weight === 'number' ? weight : parseFloat(String(weight).replace(/[^\d.]/g, ''));
                            return {
                                date: this.formatDate(new Date(app.appointmentDt), 'dd-MM-yy'),
                                weight: numericWeight,
                            };
                        });
            
                        // Filter out any null or NaN weights
                        const validWeightData = weightData.filter(item => !isNaN(item.weight));
            
                        // Update chartData state for weight data
                        this.setState(prevState => ({
                            chartData: {
                                ...prevState.chartData,
                                weight: validWeightData.map(item => item.weight),
                                weightDates: validWeightData.map(item => item.date),
                            }
                        }));
                    })
                    .catch(() => {});
            }

    
    getReportDetails(apptXReportId, reportName) {
        return fetch(C.getUrl() + `/followupAppt/appointmentReport/${apptXReportId}`)
            .then(res => res.json())
            .then(res => {
                let resObj;
                try {
                    resObj = JSON.parse(res.message);
                } catch (error) {
                    resObj = {};
                }

                let reportValues = {};

                if (reportName === 'CBC') {
                    reportValues = resObj.reportValues.reduce((acc, val) => {
                        if (val.reportParameterMstDTO.reportParamName === 'Hemoglobin') {
                            acc.hemoglobin = val.value;
                        } else if (val.reportParameterMstDTO.reportParamName === 'Platelet') {
                            acc.platelet = val.value;
                        } else if (val.reportParameterMstDTO.reportParamName === 'ANC') {
                            acc.anc = val.value;
                        }
                        return acc;
                    }, {});
                } else if (reportName === 'Biochemistry') {
                    reportValues = resObj.reportValues.reduce((acc, val) => {
                        if (val.reportParameterMstDTO.reportParamName === 'Serum Creatinine') {
                            acc.serumCreatinine = val.value;
                        }
                        return acc;
                    }, {});
                }

                const reportDate = this.formatDate(resObj.createdDt, 'dd-MM-yy');
                const existingIndex = this.state.chartData.bloodReports.labels.indexOf(reportDate);

                const updatedHemoglobin = [...this.state.chartData.bloodReports.hemoglobin];
                const updatedPlatelet = [...this.state.chartData.bloodReports.platelet];
                const updatedAnc = [...this.state.chartData.bloodReports.anc];
                const updatedLabels = [...this.state.chartData.bloodReports.labels];

                if (existingIndex > -1) {
                    updatedHemoglobin[existingIndex] = reportValues.hemoglobin || updatedHemoglobin[existingIndex];
                    updatedPlatelet[existingIndex] = reportValues.platelet || updatedPlatelet[existingIndex];
                    updatedAnc[existingIndex] = reportValues.anc || updatedAnc[existingIndex];
                } else {
                    updatedLabels.push(reportDate);
                    updatedHemoglobin.push(reportValues.hemoglobin || null);
                    updatedPlatelet.push(reportValues.platelet || null);
                    updatedAnc.push(reportValues.anc || null);
                }

                this.setState(prevState => ({
                    reportsData: {
                        ...prevState.reportsData,
                        [apptXReportId]: reportValues,
                    },
                    chartData: {
                        ...prevState.chartData,
                        bloodReports: {
                            labels: updatedLabels.sort((a, b) => new Date(a) - new Date(b)),
                            hemoglobin: updatedHemoglobin,
                            platelet: updatedPlatelet,
                            anc: updatedAnc,
                        },
                    },
                }));
            })
            .catch(() => {});
    }

    renderAppointmentsAndReports() {
        const { appointments, reportsData } = this.state;

        return appointments.map((appointment, index) => {
            const reportData = appointment.reports.map(report => reportsData[report.apptXReportId]);

            return (
                <Card className="mb-2" key={index}>
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <strong>Appointment Date:</strong> {this.formatDate(appointment.appointmentDt, 'dd-MM-yy')}
                            </Col>
                            <Col md={6}>
                                <strong>Weight:</strong> {appointment.weight || 'N/A'} kg
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <strong>Adverse Effects:</strong> {appointment.adverseEffects.map(effect => effect.reportParameterMstDTO.reportParamName).join(', ') || 'None'}
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <h5>Reports:</h5>
                                {reportData.map((data, reportIdx) => {
                                    const reportName = appointment.reports[reportIdx]?.reportMasterDTO?.reportName || 'Unknown Report';
                                    return (
                                        <div key={reportIdx}>
                                            <strong>{reportName}:</strong>
                                            {reportName === 'CBC' && (
                                                <div>
                                                    <p>Hemoglobin: {data?.hemoglobin || 'N/A'}</p>
                                                    <p>Platelet: {data?.platelet || 'N/A'}</p>
                                                    <p>ANC: {data?.anc || 'N/A'}</p>
                                                </div>
                                            )}
                                            {reportName === 'Biochemistry' && (
                                                <div>
                                                    <p>Serum Creatinine: {data?.serumCreatinine || 'N/A'}</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            );
        });
    }

    formatDate(dateString, format = 'DD-MM-YYYY') {
        const date = new Date(dateString);
        if (format === 'dd-MM-yy') {
            return date.toLocaleDateString('en-GB').replace(/\//g, '-');
        }
        return date.toLocaleDateString('en-GB');
    }

    generateSchedule(scheduleDays, chemoStartDt, cyclesPlanned, patientChemoScheduleDTOList = []) {
        // If patientChemoScheduleDTOList is available, use its dates
        if (patientChemoScheduleDTOList && patientChemoScheduleDTOList.length > 0) {
            return patientChemoScheduleDTOList.map(schedule => ({
                scheduleNum: schedule.chemoCycleNumber,
                scheduledDate: new Date(schedule.chemoDt)
            }));
        }
        // Fallback: Generate schedule based on chemoStartDt and scheduleDays
        let schedules = [];
        let nextChemoDate = new Date(chemoStartDt);     
        for (let i = 0; i < cyclesPlanned; i++) {
            let date = new Date(nextChemoDate);  // Create a new date object for each cycle
            schedules.push({
                scheduleNum: i + 1,
                scheduledDate: date
            });
            nextChemoDate.setDate(nextChemoDate.getDate() + scheduleDays);  // Move to next cycle date
        }
        return schedules;
    }
    

    getKhoranaScore(patientId) {
        return fetch(C.getUrl() + '/protocol/getKhoranaScore/' + patientId)
            .then(res => res.json())
            .then(res => {
                if (res.statusCode === 200 && res.data != null) {
                    this.setState({ khoranaScore: res.data });
                }
            })
            .catch(err => {
            });
    }
    
    reportHandler = (mode, data) => {
        let isThisValidForm = true;
        if (isThisValidForm) {
            switch (mode) {
                case Constants.MODE_NEW_REPORT:
                case Constants.MODE_VIEW_REPORT:
                    this.setState({
                        reportModal: true,
                        reportMode: mode,
                        selectedApptReportData: data
                    });
                    break;

                default:
                    break;
            }
        }
    }

    closeReportHandler = () => {
        this.setState({ reportModal: false, reportMode: null });
    }
    render() {
        const {  patientData, loading, chartData, generatedSchedules, activeTreatmentPlans, chemoSittingDetails, khoranaScore, initialAssessmentDetails, appointments, chemoTemplateDetails, reportModal, selectedApptReportData, reportMode, reportsData } = this.state;
    
        if (loading) {
            return <Spinner animation="border" />;
        }
    
        if (!patientData) {
            return <p>No patient data available.</p>;
        }
    
        return (
            <Container fluid className="p-2">
                {/* Patient Details Card */}
                <Card className="mb-2">
                    <Card.Body>
                        <Row className="mb-1">
                            <Col md={4} className="border-right"><strong>Name:</strong> {`${patientData.firstName} ${patientData.middleName ?? ''} ${patientData.lastName}`}</Col>
                            <Col md={4} className="border-right"><strong>DOB:</strong> {patientData.dob}</Col>
                            <Col md={4} className="border-right"><strong>Age:</strong> {patientData.age ?? 'N/A'} Years</Col>
                            <Col md={4} className="border-right"><strong>Gender:</strong> {patientData.genderMstDto.genderName ?? 'N/A'}</Col>
                            <Col md={4} className="border-right"><strong>Height:</strong> {patientData.height ?? 'N/A'} cm</Col>
                            <Col md={4} className="border-right"><strong>Weight:</strong> {patientData.weight ?? 'N/A'} kg</Col>
                            <Col md={4} className="border-right"><strong>Mutation:</strong> {initialAssessmentDetails?.patientMutationXrefList?.[0]?.mutationMstDTO.mutationName ?? 'N/A'}</Col>
                            <Col md={4} className="border-right"><strong>Stage ID:</strong> {initialAssessmentDetails?.stageMasterDTO?.stageId ?? 'N/A'}</Col>
                            <Col md={4} className="border-right"><strong>Limb Loss:</strong> {initialAssessmentDetails?.patientLimbLossDTOList?.map(loss => loss.limbLossMasterDTO.limbLossName).join(', ') || 'N/A'}</Col>
                            <Col md={4}><strong>Allergies:</strong> {patientData.patientAllergies?.map(allergy => allergy.allergiesMstDTO.allergiesName).join(', ') || 'None'}</Col>
                            <Col md={4}><strong>Comorbidities:</strong> {patientData.patientComorbidities?.map(comorbidity => comorbidity.comorbiditiesDto.comorbiditiesName).join(', ') || 'None'}</Col>
                            <Col md={4}><strong>Doctor's Observation:</strong> {initialAssessmentDetails?.doctorObservation || 'N/A'}</Col>
                            <Col md={4}><strong>Pathology:</strong> {initialAssessmentDetails?.pathologyText || 'N/A'}</Col>
                            <Col md={4}><strong>Doctor's Prescription:</strong> {initialAssessmentDetails?.doctorPrescription || 'N/A'}</Col>
                            <Col md={4} className="border-right"><strong>Patient ID:</strong> {patientData.patientID}</Col>
                            <Col md={4} className="border-right"><strong>Current Cancer:</strong> {initialAssessmentDetails?.patientCancerSiteDTOList?.[0]?.cancerSiteMstDto.cancerSiteName ?? 'N/A'}</Col>
                            <Col md={4} className="border-right"><strong>Cancer History:</strong> {initialAssessmentDetails?.patientPrevCancerSiteDTOList?.[0]?.cancerSiteMstDTO.cancerSiteName ?? 'N/A'}</Col>
                        </Row>
                    </Card.Body>
                </Card>
    
                {/* Charts for ANC, Hemoglobin, Platelet, and Weight */}
<Card className="mb-2 chart-card">
    <Card.Body className="chart-card-body">
        <Row className="mb-1">
            <Col xs={12} md={6} lg={3} className="text-center"><strong style={{ fontSize: '13px' }}>ANC</strong></Col>
            <Col xs={12} md={6} lg={3} className="text-center"><strong style={{ fontSize: '13px' }}>Hemoglobin</strong></Col>
            <Col xs={12} md={6} lg={3} className="text-center"><strong style={{ fontSize: '13px' }}>Platelet</strong></Col>
            <Col xs={12} md={6} lg={3} className="text-center"><strong style={{ fontSize: '13px' }}>Weight (kg)</strong></Col>
        </Row>
        <Row>
            {/* ANC Chart */}
            <Col xs={12} md={6} lg={3} className="mb-2">
                <div className="chart-container">
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            data={chartData?.bloodReports?.labels?.map((label, index) => ({
                                label,
                                anc: chartData?.bloodReports?.anc[index] ?? null,
                            })) || []}
                            margin={{ top: 20, right: 40, left: -20, bottom: 20 }}
                        >
                            <XAxis dataKey="label" tick={{ fontSize: 4 }} angle={-45} textAnchor="end" height={50} />
                            <YAxis domain={[0, 4000]} tick={{ fontSize: 4 }} />
                            <Tooltip 
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '5px' }}
                                labelStyle={{ fontWeight: 'bold', fontSize: 4 }}
                            />
                            <Line type="monotone" dataKey="anc" stroke="#FF6384" dot={{ strokeWidth: 2 }} activeDot={{ r: 8 }} connectNulls />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Col>

            {/* Hemoglobin Chart */}
            <Col xs={12} md={6} lg={3} className="mb-2">
                <div className="chart-container">
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            data={chartData?.bloodReports?.labels?.map((label, index) => ({
                                label,
                                hemoglobin: chartData?.bloodReports?.hemoglobin[index] ?? null,
                            })) || []}
                            margin={{ top: 20, right: 40, left: -20, bottom: 20 }}
                        >
                            <XAxis dataKey="label" tick={{ fontSize: 4 }} angle={-45} textAnchor="end" height={50} />
                            <YAxis domain={[8, 18]} tick={{ fontSize: 4 }} />
                            <Tooltip 
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '5px' }}
                                labelStyle={{ fontWeight: 'bold', fontSize: 4 }}
                            />
                            <Line type="monotone" dataKey="hemoglobin" stroke="#4BC0C0" dot={{ strokeWidth: 2 }} activeDot={{ r: 8 }} connectNulls />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Col>

            {/* Platelet Chart */}
            <Col xs={12} md={6} lg={3} className="mb-2">
                <div className="chart-container">
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            data={chartData?.bloodReports?.labels?.map((label, index) => ({
                                label,
                                platelet: chartData?.bloodReports?.platelet[index] ?? null,
                            })) || []}
                            margin={{ top: 20, right: 40, left: -20, bottom: 20 }}
                        >
                            <XAxis dataKey="label" tick={{ fontSize: 4 }} angle={-45} textAnchor="end" height={50} />
                            <YAxis domain={[0, 300000]} tick={{ fontSize: 4 }} />
                            <Tooltip 
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '5px' }}
                                labelStyle={{ fontWeight: 'bold', fontSize: 4 }}
                            />
                            <Line type="monotone" dataKey="platelet" stroke="#9966FF" dot={{ strokeWidth: 2 }} activeDot={{ r: 8 }} connectNulls />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Col>

            {/* Weight Chart */}
            <Col xs={12} md={6} lg={3} className="mb-2">
                <div className="chart-container">
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            data={chartData?.weightDates?.map((date, index) => ({
                                date,
                                weight: chartData?.weight[index] ?? null,
                            })) || []}
                            margin={{ top: 20, right: 40, left: -20, bottom: 20 }}
                        >
                            <XAxis dataKey="date" tick={{ fontSize: 4 }} angle={-45} textAnchor="end" height={50} />
                            <YAxis domain={[30, 120]} tick={{ fontSize: 4 }} />
                            <Tooltip 
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '5px' }}
                                labelStyle={{ fontWeight: 'bold', fontSize: 4 }}
                            />
                            <Line type="monotone" dataKey="weight" stroke="#FFCE56" dot={{ strokeWidth: 2 }} activeDot={{ r: 8 }} connectNulls />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Col>
        </Row>
    </Card.Body>
</Card>

    
                {/* Schedule */}
                <Card className="mb-2">
            <Card.Header as="h4" className="font-weight-bold">Schedule</Card.Header>
            <Card.Body style={{ padding: '10px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {Object.values(generatedSchedules).flat().sort((a, b) => a.scheduleNum - b.scheduleNum).map((schedule, index) => (
                        <div key={index} style={{ padding: '5px', border: '1px solid #dee2e6', borderRadius: '5px', backgroundColor: '#f8f9fa' }}>
                            <strong>{schedule.scheduleNum}-</strong> {moment(schedule.scheduledDate).format('DD-MM-YYYY')}
                        </div>
                    ))}
                </div>
            </Card.Body>
        </Card>
    
                <Card className="mb-2">
    <Card.Header as="h4" className="font-weight-bold">Chemo Sitting & Appointment Details</Card.Header>
    <Card.Body style={{ padding: '10px' }}>
        <Row>
            {/* Loop through the maximum of chemoSittingDetails and appointments */}
            {[...Array(Math.max(chemoSittingDetails.length, appointments.length))]
                .map((_, index) => (
                    <React.Fragment key={index}>
                        <Col md={9}>
                            {/* Chemo Sitting Card - sorted by date */}
                            {chemoSittingDetails.sort((a, b) => new Date(b.chemoDt) - new Date(a.chemoDt))[index] ? (
                                <Card className="mb-2">
                                    <Card.Body style={{ padding: '8px' }}>
                                        <Row>
                                            <Col md={4}>
                                                <strong>Date:</strong> {this.formatDate(chemoSittingDetails[index].chemoDt, 'DD-MM-YYYY')} <br />
                                                <strong>Pre-Medications:</strong> {chemoSittingDetails[index].premedications || 'N/A'} <br />
                                                <strong>Supplements:</strong> {chemoSittingDetails[index].supplements || 'N/A'} <br />
                                                <strong>Protocol Name:</strong> {chemoTemplateDetails[chemoSittingDetails[index].patChemoSittingId]?.protocolName || 'N/A'} <br />
                                                <strong>Antiemetic Drugs:</strong> {chemoSittingDetails[index].antiemeticDrugs || 'N/A'} <br />
                                                <strong>Antithrombotic Drugs:</strong> {chemoSittingDetails[index].antithromboticDrugs || 'N/A'} <br />
                                                <strong>FnRisk & GCSF Medicine:</strong> {chemoTemplateDetails[chemoSittingDetails[index].patChemoSittingId]?.fnRisk || 'N/A'} <br />
                                            </Col>
                                            <Col md={8}>
                                                <strong>Chemotherapy Dose:</strong> {chemoTemplateDetails[chemoSittingDetails[index].patChemoSittingId]?.instructions || 'N/A'} <br />
                                                <strong>Drug Interactions:</strong> {chemoTemplateDetails[chemoSittingDetails[index].patChemoSittingId]?.drugInteractions || 'N/A'} <br />
                                                <strong>Dose And Method Of Administration:</strong> {chemoTemplateDetails[chemoSittingDetails[index].patChemoSittingId]?.chemotherapyDoseAndMethodOfAdministration || 'N/A'} <br />
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            ) : (
                                <div style={{ height: '160px' }} /> // Empty space if no chemo sitting card
                            )}
                        </Col>

                        <Col md={3}>
                            {/* Appointment Card - sorted by date */}
                            {appointments.sort((a, b) => new Date(b.appointmentDt) - new Date(a.appointmentDt))[index] ? (
                                <Card className="mb-3">
                                    <Card.Body style={{ padding: '8px' }}>
                                        <Row>
                                            {/* Left Column: Appointment Details */}
                                            <Col md={8} style={{ padding: '1', margin: '1' }}>
                                                <p style={{ fontSize: '13px', margin: '4px 0' }}>
                                                    <strong>Appointment Date:</strong> {this.formatDate(appointments[index].appointmentDt, 'dd-MM-yy')}
                                                </p>
                                                <p style={{ fontSize: '13px', margin: '4px 0' }}>
                                                    <strong>Weight:</strong> {appointments[index].weight || 'N/A'} kg
                                                </p>
                                                <p style={{ fontSize: '13px', margin: '4px 0' }}>
                                                    <strong>Adverse Effects:</strong> {appointments[index].adverseEffects.map(effect => effect.reportParameterMstDTO.reportParamName).join(', ') || 'None'}
                                                </p>

                                                <p style={{ fontSize: '13px', margin: '4px 0' }}>
                                                    <strong>Khorana Score:</strong> {this.state.khoranaScore === '' || this.state.khoranaScore == null ? 'N/A' : this.state.khoranaScore}
                                                </p>
                                            </Col>

                                            {/* Right Column: Blood Reports */}
                                            <Col md={4} style={{ padding: '0', margin: '0' }}>
                                            {appointments[index].reports.map((report, reportIdx) => {
                                                const reportName = report?.reportMasterDTO?.reportName || 'Unknown Report';
                                                const reportData = this.state.reportsData[report.apptXReportId] || {}; // Access the report data from state
                                                return (
                                                    <div key={reportIdx} style={{ marginBottom: '4px' }}>
                                                        {reportName === 'CBC' && (
                                                            <>
                                                                <p style={{ fontSize: '13px', margin: '2px 0' }}><strong>CBC</strong></p>
                                                                <p style={{ fontSize: '12px', margin: '2px 0' }}>Hemoglobin: {reportData?.hemoglobin || 'N/A'}</p>
                                                                <p style={{ fontSize: '12px', margin: '2px 0' }}>Platelet: {reportData?.platelet || 'N/A'}</p>
                                                                <p style={{ fontSize: '12px', margin: '2px 0' }}>ANC: {reportData?.anc || 'N/A'}</p>
                                                            </>
                                                        )}
                                                        {reportName === 'Biochemistry' && (
                                                            <>
                                                                <p style={{ fontSize: '13px', margin: '2px 0' }}><strong>Biochemistry</strong></p>
                                                                <p style={{ fontSize: '12px', margin: '2px 0' }}>Serum Creatinine: {reportData?.serumCreatinine || 'N/A'}</p>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </Col>

                                        </Row>
                                    </Card.Body>
                                </Card>
                            ) : (
                                <div style={{ height: '160px' }} /> // Empty space if no appointment card
                            )}
                        </Col>
                    </React.Fragment>
                ))}
        </Row>
    </Card.Body>
</Card>



            </Container>
        
        );
    }
    
}    
